import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import { ArrowRight, Heart, MessageCircle, Phone, Send, Sparkles, UserRound, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { openExternal } from "@/lib/openExternal";
import { streamChat, type ChatMsg } from "@/lib/aiChatStream";
import { isSafeAction, parseActions, shouldOfferHuman, type Action, type ActionKind } from "@/lib/aiActions";

const STARTERS = [
  "How can I donate?",
  "Show me active projects",
  "How do I volunteer?",
  "Talk to a human",
];

const GREETING: ChatMsg = {
  role: "assistant",
  content:
    "Hi! I'm the **Raise Sri Lanka** AI assistant. Ask me about our projects, donating, volunteering, or our 501(c)(3) status — I can also point you straight to the right page.",
};

function actionIcon(kind: ActionKind) {
  switch (kind) {
    case "whatsapp":
      return Phone;
    case "messenger":
      return MessageCircle;
    case "email":
      return UserRound;
    default:
      return ArrowRight;
  }
}

export function AiChatPanel({ onClose }: { onClose: () => void }) {
  const [messages, setMessages] = useState<ChatMsg[]>([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHuman, setShowHuman] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data: settings } = useQuery({
    queryKey: ["site_settings_ai_chat"],
    queryFn: async () => {
      const { data } = await supabase.from("site_settings").select("key,value");
      return Object.fromEntries((data ?? []).map((r) => [r.key, r.value])) as Record<string, string>;
    },
    staleTime: 60_000,
  });

  const whatsapp = settings?.whatsapp_url;
  const messenger = settings?.messenger_url;
  const contactEmail = settings?.contact_email;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const runAction = (a: Action) => {
    if (!isSafeAction(a)) return;
    if (a.kind === "link" && a.href) {
      navigate(a.href);
      onClose();
      return;
    }
    if (a.kind === "external" && a.href) {
      openExternal(a.href);
      return;
    }
    if (a.kind === "whatsapp" && whatsapp) {
      openExternal(whatsapp);
      return;
    }
    if (a.kind === "messenger" && messenger) {
      openExternal(messenger);
      return;
    }
    if (a.kind === "email" && contactEmail) {
      window.location.href = `mailto:${contactEmail}`;
    }
  };

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || busy) return;

    // Quick shortcut: "Talk to a human" reveals the human popover instead of asking AI.
    if (trimmed.toLowerCase() === "talk to a human") {
      setShowHuman(true);
      return;
    }

    const userMsg: ChatMsg = { role: "user", content: trimmed };
    const next = [...messages, userMsg];
    setMessages([...next, { role: "assistant", content: "" }]);
    setInput("");
    setBusy(true);

    let acc = "";
    await streamChat({
      messages: next.filter((m) => m.role !== "assistant" || m.content.length > 0 || m === GREETING).slice(-12),
      onDelta: (chunk) => {
        acc += chunk;
        setMessages((prev) => {
          const copy = prev.slice();
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      },
      onDone: () => setBusy(false),
      onError: ({ message }) => {
        setMessages((prev) => {
          const copy = prev.slice();
          copy[copy.length - 1] = {
            role: "assistant",
            content: `_${message}_\n\nFor anything urgent, please tap **Talk to a human**.`,
          };
          return copy;
        });
        setBusy(false);
      },
    });
  };

  return (
    <div className="flex h-[520px] w-[min(380px,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-soft">
      {/* Header */}
      <div className="relative flex items-center gap-2 border-b border-border bg-card px-3 py-2.5">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="flex-1 leading-tight">
          <p className="text-sm font-semibold">Ask Raise Sri Lanka</p>
          <p className="text-[11px] text-muted-foreground">AI assistant · grounded in our site</p>
        </div>
        <button
          type="button"
          onClick={() => setShowHuman((v) => !v)}
          aria-expanded={showHuman}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted"
        >
          <UserRound className="h-3.5 w-3.5" />
          Talk to a human
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close chat"
          className="ml-1 inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>

        {showHuman && (
          <div className="absolute right-3 top-full z-10 mt-2 w-60 rounded-xl border border-border bg-popover p-2 shadow-soft">
            <p className="px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              Reach a volunteer
            </p>
            {whatsapp && (
              <a
                href={whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowHuman(false)}
                className="flex w-full items-center gap-2 rounded-lg bg-[#25d366] px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
              >
                <Phone className="h-4 w-4" /> WhatsApp
              </a>
            )}
            {messenger && (
              <a
                href={messenger}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowHuman(false)}
                className="mt-1.5 flex w-full items-center gap-2 rounded-lg bg-[#0084ff] px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
              >
                <MessageCircle className="h-4 w-4" /> Messenger
              </a>
            )}
            {contactEmail && (
              <a
                href={`mailto:${contactEmail}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowHuman(false)}
                className="mt-1.5 flex w-full items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
              >
                <Heart className="h-4 w-4" /> Email us
              </a>
            )}
            {!whatsapp && !messenger && !contactEmail && (
              <p className="px-2 py-2 text-xs text-muted-foreground">No contact channels are configured yet.</p>
            )}
          </div>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-3 py-3">
        {messages.map((m, i) => (
          <MessageBubble key={i} msg={m} onAction={runAction} onTalkToHuman={() => setShowHuman(true)} />
        ))}

        {messages.length === 1 && !busy && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {STARTERS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => send(s)}
                className="rounded-full border border-border bg-card px-3 py-1 text-xs text-foreground transition hover:bg-muted"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Input */}
      <form
        className="border-t border-border bg-card p-2"
        onSubmit={(e) => {
          e.preventDefault();
          send(input);
        }}
      >
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send(input);
              }
            }}
            rows={1}
            placeholder="Ask anything…"
            className="max-h-24 flex-1 resize-none rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
          />
          <button
            type="submit"
            disabled={busy || !input.trim()}
            aria-label="Send"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-1.5 px-1 text-[10px] text-muted-foreground">
          AI answers based on our site. Tap <span className="font-semibold">Talk to a human</span> for urgent help.
        </p>
      </form>
    </div>
  );
}

function MessageBubble({
  msg,
  onAction,
  onTalkToHuman,
}: {
  msg: ChatMsg;
  onAction: (a: Action) => void;
  onTalkToHuman: () => void;
}) {
  const isUser = msg.role === "user";
  const { text, actions } = useMemo(
    () => (isUser ? { text: msg.content, actions: [] as Action[] } : parseActions(msg.content)),
    [msg.content, isUser],
  );
  const offerHuman = !isUser && !!text && shouldOfferHuman(text);


  return (
    <div className={cn("flex flex-col gap-1.5", isUser ? "items-end" : "items-start")}>
      <div
        className={cn(
          "max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed",
          isUser ? "bg-primary text-primary-foreground" : "bg-muted text-foreground",
        )}
      >
        {isUser ? (
          <span className="whitespace-pre-wrap">{msg.content}</span>
        ) : text ? (
          <div className="prose prose-sm max-w-none dark:prose-invert prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-a:text-primary">
            <ReactMarkdown>{text}</ReactMarkdown>
          </div>
        ) : (
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current [animation-delay:120ms]" />
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current [animation-delay:240ms]" />
          </span>
        )}
      </div>

      {!isUser && actions.length > 0 && (
        <div className="flex max-w-[85%] flex-wrap gap-1.5">
          {actions.map((a, i) => {
            const Icon = actionIcon(a.kind);
            const isHuman = a.kind === "whatsapp" || a.kind === "messenger";
            return (
              <button
                key={i}
                type="button"
                onClick={() => onAction(a)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition",
                  isHuman
                    ? a.kind === "whatsapp"
                      ? "bg-[#25d366] text-white hover:opacity-90"
                      : "bg-[#0084ff] text-white hover:opacity-90"
                    : "bg-primary text-primary-foreground hover:opacity-90",
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {a.label}
              </button>
            );
          })}
        </div>
      )}

      {offerHuman && (
        <button
          type="button"
          onClick={onTalkToHuman}
          className="inline-flex max-w-[85%] items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary transition hover:bg-primary/20"
        >
          <UserRound className="h-3.5 w-3.5" />
          Talk to a human
        </button>
      )}
    </div>
  );
}
